/**
 * Build ID storage utilities
 */

import { envStorage } from "@/lib/storage/envIsolatedStorage";

const STORAGE_KEY = "app-build-id";
const LAST_CHECK_KEY = "app-build-id-last-check";

export const buildIdStorage = {
  /**
   * Get current build ID from localStorage
   */
  getCurrentBuildId(): string | null {
    try {
      return envStorage.getItem(STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to get build ID from localStorage:", error);
      return null;
    }
  },

  /**
   * Set current build ID to localStorage
   */
  setCurrentBuildId(buildId: string): void {
    try {
      envStorage.setItem(STORAGE_KEY, buildId);
      // Also record the last check time
      this.setLastCheckTime(new Date());
    } catch (error) {
      console.error("Failed to save build ID to localStorage:", error);
    }
  },

  /**
   * Clear build ID from localStorage
   */
  clearBuildId(): void {
    try {
      envStorage.removeItem(STORAGE_KEY);
      envStorage.removeItem(LAST_CHECK_KEY);
    } catch (error) {
      console.error("Failed to clear build ID from localStorage:", error);
    }
  },

  /**
   * Get the last check time
   */
  getLastCheckTime(): Date | null {
    try {
      const timestamp = envStorage.getItem(LAST_CHECK_KEY);
      return timestamp ? new Date(parseInt(timestamp, 10)) : null;
    } catch (error) {
      console.warn("Failed to get last check time from localStorage:", error);
      return null;
    }
  },

  /**
   * Set the last check time
   */
  setLastCheckTime(date: Date): void {
    try {
      envStorage.setItem(LAST_CHECK_KEY, date.getTime().toString());
    } catch (error) {
      console.error("Failed to save last check time to localStorage:", error);
    }
  },

  /**
   * Check if update check should be performed now (based on time interval)
   * @param intervalMs Check interval in milliseconds
   */
  shouldCheckNow(intervalMs: number): boolean {
    const lastCheck = this.getLastCheckTime();
    if (!lastCheck) return true;

    const now = new Date();
    const timeSinceLastCheck = now.getTime() - lastCheck.getTime();
    return timeSinceLastCheck >= intervalMs;
  },
};
